import { type ContractViolationError, createSisumailClient } from '@sisumail/api-client';
import { API_BASE_URL } from '@sisumail/config';
import { toast } from 'sonner';

const API_URL = import.meta.env.VITE_API_URL || API_BASE_URL;

/**
 * Handle Contract Violations globally
 */
const handleContractViolation = (error: ContractViolationError) => {
    console.error('[Contract Violation]', error);

    // Differentiate between generic validation error and version mismatch
    if (error.name === 'ContractVersionMismatchError') {
        toast.warning('App Update Available', {
            description: 'A new version of the app is available. Please update to continue.',
            action: {
                label: 'Update',
                onClick: () => window.location.reload(),
            },
            duration: Infinity,
        });
        return;
    }

    // Standard Schema Violation (Dev/Preview only usually)
    if (import.meta.env.DEV || import.meta.env.MODE === 'preview') {
        toast.error('API Contract Violation', {
            description: error.getSummary ? error.getSummary() : error.message,
            duration: 5000,
        });
    }
};

// 1. Shared Anonymous Client (for public endpoints)
export const api = createSisumailClient({
    baseUrl: API_URL,
    env: (import.meta.env.MODE === 'production' ? 'production' : 'development') as
        | 'production'
        | 'development',
    onVersionMismatch: () =>
        handleContractViolation(
            Object.assign(new Error('Version mismatch'), {
                name: 'ContractVersionMismatchError',
            }) as any
        ),
    onSchemaViolation: handleContractViolation,
    onError: (error: { code: string; message: string }) => {
        // Don't toast 401s if they are handled by auth logic
        if (error.code !== 'UNAUTHORIZED') {
            toast.error(error.message || 'An error occurred');
        }
    },
});

// 2. Scoped Client Helper (for authenticated requests)
export function createScopedClient(deviceKey: { id: string; secret: string }) {
    return createSisumailClient({
        baseUrl: API_URL,
        env: (import.meta.env.MODE === 'production' ? 'production' : 'development') as
            | 'production'
            | 'development',
        deviceKey,
        onVersionMismatch: () =>
            handleContractViolation(
                Object.assign(new Error('Version mismatch'), {
                    name: 'ContractVersionMismatchError',
                }) as any
            ),
        onSchemaViolation: handleContractViolation,
        onError: (error: { code: string; message: string }) => {
            if (error.code !== 'UNAUTHORIZED') {
                toast.error(error.message || 'An error occurred');
            }
        },
    });
}

// 3. Initial Handshake (Run on app init)
export function initApiHandshake() {
    api.checkVersion().catch((err: Error) => {
        console.warn('Initial Handshake Failed:', err);
        // Violation handler will manage UI if it was a mismatch
    });
}
